import React from 'react';
import { BarChart3Icon, UsersIcon, MailOpenIcon, MessageCircleIcon, PercentIcon, StarIcon, ArrowUpIcon, SmartphoneIcon, MessageSquareIcon, MailIcon, PhoneIcon, MonitorIcon, Share2Icon, PlusIcon, MinusIcon, ArrowRightIcon } from 'lucide-react';
import { kpis, channelRows, districts, districtInfo, topics, analyticsReco, type Bi } from '../../../data/communicationsAnalytics';
type P = {
  isFR: boolean;
};
const pk = (isFR: boolean) => (v: Bi) => isFR ? v.fr : v.en;
const pct = (isFR: boolean, n: number | string) => isFR ? `${n} %` : `${n}%`;
const KPI_ICONS = {
  sent: BarChart3Icon,
  reached: UsersIcon,
  open: MailOpenIcon,
  interactions: MessageCircleIcon,
  calls: PercentIcon,
  satisfaction: StarIcon
} as const;
export function KpiRow({
  isFR
}: P) {
  const pick = pk(isFR);
  return <div className="grid grid-cols-2 sm:grid-cols-3 2xl:grid-cols-6 gap-2.5" data-id="element-2113" data-mp="t05dd0yuifbk">
      {kpis.map(k => {
      const Icon = KPI_ICONS[k.key];
      return <div key={k.key} className="rounded-xl border border-[#EEEEF4] bg-white px-3.5 py-3 min-w-0" data-id="element-2114" data-mp="2x8yu63sld8ok">
            <p className="flex items-center gap-2 font-sans text-[11px] text-[#3A4050]" data-id="element-2115" data-mp="31a0ny53qosj5">
              <span className="w-6 h-6 rounded-md bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-2116" data-mp="369mxo4kczqn7">
                <Icon className="w-3.5 h-3.5 text-[#4F46E5]" strokeWidth={1.8} data-id="element-2117" data-mp="3b8q15kkoxot2" />
              </span>
              <span className="truncate" data-id="element-2118" data-mp="2dlr3p7m6ccu4">{pick(k.label)}</span>
            </p>
            <p className="flex items-center gap-2 mt-2.5" data-id="element-2119" data-mp="2ijj46q0vx79x">
              <span className="font-sans text-[21px] font-semibold text-[#121212] leading-none tabular-nums whitespace-nowrap" data-id="element-2120" data-mp="2ihpybcy3ir2z">{pick(k.value)}</span>
              {k.trend && <span className="inline-flex items-center gap-0.5 rounded bg-[#EAF7EE] px-1.5 py-0.5 font-sans text-[10px] text-[#1E8A45] whitespace-nowrap" data-id="element-2121" data-mp="2sb6s4gy8g66n">
                  <ArrowUpIcon className="w-2.5 h-2.5" aria-hidden="true" data-id="element-2122" data-mp="1zsrpd8y4ooo5" />
                  {isFR ? k.trend : k.trend.replace(' %', '%')}
                </span>}
            </p>
            <p className="font-sans text-[10.5px] text-[#6B7180] mt-1.5 truncate" data-id="element-2123" data-mp="1nr9vk510zpvg">{pick(k.sub)}</p>
          </div>;
    })}
    </div>;
}
const CH_ICONS = {
  push: SmartphoneIcon,
  sms: MessageSquareIcon,
  email: MailIcon,
  voice: PhoneIcon,
  web: MonitorIcon,
  social: Share2Icon
} as const;
function Bar({
  value,
  tone
}: {
  value: number;
  tone: 'indigo' | 'green';
}) {
  return <span className="flex-1 h-1.5 rounded-full bg-[#EEEFF4] overflow-hidden min-w-[28px]" aria-hidden="true" data-id="element-2124" data-mp="18vkgyry66f0r">
      <span className={`block h-full rounded-full ${tone === 'indigo' ? 'bg-[#4F46E5]' : 'bg-[#22A04F]'}`} style={{
      width: `${value}%`
    }} data-id="element-2125" data-mp="1irx7jgyf4cra" />
    </span>;
}
export function ChannelPerformance({
  isFR
}: P) {
  const pick = pk(isFR);
  return <div className="h-full rounded-xl border border-[#EEEEF4] bg-white p-4 overflow-x-auto" data-id="element-2126" data-mp="2u5sp0wcsoxlo">
      <p className="font-sans text-[13.5px] font-semibold text-[#121212]" data-id="element-2127" data-mp="2p6xzlbkboasl">{isFR ? 'Performance par canal' : 'Performance by channel'}</p>
      <table className="w-full mt-3 font-sans text-[11px]" data-id="element-2128" data-mp="b2ck83cjw1zh">
        <thead data-id="element-2129" data-mp="g0dxu8r7j93k">
          <tr className="text-left text-[9.5px] font-semibold tracking-[0.1em] uppercase text-[#6B7180]" data-id="element-2130" data-mp="g0w3tx3o91oo">
            <th className="pb-2 font-semibold" data-id="element-2131" data-mp="21ecb9yzrabm">{isFR ? 'Canal' : 'Channel'}</th>
            <th className="pb-2 font-semibold" data-id="element-2132" data-mp="6zfvrw0bxbnm">{isFR ? 'Envoyés' : 'Sent'}</th>
            <th className="pb-2 font-semibold" data-id="element-2133" data-mp="unz42bjdpofp">{isFR ? 'Ouverture' : 'Open'}</th>
            <th className="pb-2 font-semibold" data-id="element-2134" data-mp="ypkmeceomuxa">Interaction</th>
          </tr>
        </thead>
        <tbody data-id="element-2135" data-mp="kyfmd97ek0mf">
          {channelRows.map(r => {
          const Icon = CH_ICONS[r.key];
          return <tr key={r.key} className="text-[#121212]" data-id="element-2136" data-mp="pwz93bttlgfu">
                <td className="py-2 pr-2" data-id="element-2137" data-mp="pvee5l3lfvhg">
                  <span className="flex items-center gap-2 whitespace-nowrap" data-id="element-2138" data-mp="1dmtghdvdecsp">
                    <Icon className="w-3.5 h-3.5 text-[#4F46E5] shrink-0" strokeWidth={1.8} aria-hidden="true" data-id="element-2139" data-mp="1idjokyv6ktr3" />
                    {pick(r.label)}
                  </span>
                </td>
                <td className="py-2 pr-2 tabular-nums whitespace-nowrap" data-id="element-2140" data-mp="3kya1irr1fnjl">{pick(r.sent)}</td>
                <td className="py-2 pr-3" data-id="element-2141" data-mp="3gwoqlby2djc1">
                  <span className="flex items-center gap-2" data-id="element-2142" data-mp="3gxq0hd3cfzs6">
                    <span className="w-8 tabular-nums whitespace-nowrap" data-id="element-2143" data-mp="3bxvarcvb1g8p">{pct(isFR, r.open)}</span>
                    <Bar value={r.open} tone="indigo" data-id="element-2144" data-mp="ci8fx76tsb1w" />
                  </span>
                </td>
                <td className="py-2" data-id="element-2145" data-mp="8h502kos2f0o">
                  <span className="flex items-center gap-2" data-id="element-2146" data-mp="8i6bwrdmb5jg">
                    <span className="w-8 tabular-nums whitespace-nowrap" data-id="element-2147" data-mp="3pws7t33j1pm">{pct(isFR, r.interaction)}</span>
                    <Bar value={r.interaction * 2.5} tone="green" data-id="element-2148" data-mp="3uwzi8h7771y9" />
                  </span>
                </td>
              </tr>;
        })}
        </tbody>
      </table>
    </div>;
}
function seeded(n: number) {
  let s = 11;
  const out: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    s = (s * 9301 + 49297) % 233280;
    const a = s / 233280;
    s = (s * 9301 + 49297) % 233280;
    const b = s / 233280;
    out.push([70 + a * 150, 60 + b * 170]);
  }
  return out;
}
const DOTS = seeded(90);
export function GeographicCoverage({
  isFR
}: P) {
  const pick = pk(isFR);
  return <div className="h-full rounded-xl border border-[#EEEEF4] bg-white p-4 flex flex-col" data-id="element-2149" data-mp="1cr6gyn65ixfw">
      <p className="font-sans text-[13.5px] font-semibold text-[#121212]" data-id="element-2150" data-mp="1hq1bunhe8cgd">{isFR ? 'Couverture géographique' : 'Geographic coverage'}</p>
      <div className="mt-3 flex flex-wrap gap-1.5" role="tablist" aria-label={isFR ? 'Secteurs' : 'Sectors'} data-id="element-2151" data-mp="1mnjj89frijm9">
        {districts.map((d, i) => <span key={d.key} role="tab" aria-selected={i === 0} className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-sans text-[10px] whitespace-nowrap ${i === 0 ? 'bg-[#1B1D3A] text-white' : 'bg-[#F4F5F8] text-[#3A4050]'}`} data-id="element-2152" data-mp="1wenxf6xm60g1" data-mpi={i}>
            <span className="w-1.5 h-1.5 rounded-full" style={{
          backgroundColor: i === 0 ? '#8B7FF7' : d.color
        }} aria-hidden="true" data-id="element-2153" data-mp="5cb8laxv0ene" />
            {isFR ? 'Secteur' : 'Sector'} {d.key}
          </span>)}
      </div>
      <div className="relative mt-3 flex-1 min-h-[220px] rounded-lg overflow-hidden bg-[#F3F4F2]" data-id="element-2154" data-mp="f3nvrp8pqgn3">
        <svg viewBox="0 0 360 260" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 w-full h-full" aria-hidden="true" data-id="element-2155" data-mp="aamg3f0omd2g">
          <rect width="360" height="260" fill="#F2F3F1" data-id="element-2156" data-mp="p2wq4j7hpx9z" />
          <g stroke="#FFFFFF" strokeWidth="1.4" data-id="element-2157" data-mp="k17ccvsvv5i7">
            {Array.from({
            length: 20
          }).map((_, i) => <line key={`v${i}`} x1={i * 20} y1="0" x2={i * 20 - 30} y2="260" data-id="element-2158" data-mp="y18d8b95ecxo" data-mpi={i} />)}
            {Array.from({
            length: 16
          }).map((_, i) => <line key={`h${i}`} x1="0" y1={i * 18} x2="360" y2={i * 18 + 14} data-id="element-2159" data-mp="2zsvagujmv42l" data-mpi={i} />)}
          </g>
          <g stroke="#FFFFFF" strokeWidth="3.5" data-id="element-2160" data-mp="2uuk08ipferju">
            <line x1="0" y1="120" x2="360" y2="140" data-id="element-2161" data-mp="2uvv38gb1rv9w" />
            <line x1="130" y1="0" x2="105" y2="260" data-id="element-2162" data-mp="1abpx73uq031h" />
          </g>
          <path d="M240 0 C 250 60, 300 90, 290 150 C 282 200, 320 230, 360 250 L360 0 Z" fill="#CFE0F3" data-id="element-2163" data-mp="1fb2t0e3kkkg7" />
          <path d="M70 70 L150 44 L205 70 L225 150 L200 235 L110 250 L60 190 Z" fill="#6D5DF6" fillOpacity="0.28" stroke="#5B4BE0" strokeWidth="1.2" data-id="element-2164" data-mp="11jxph0x2z80l" />
          {DOTS.map(([x, y], i) => <circle key={i} cx={x} cy={y} r="1.8" fill="#3E2FC9" opacity="0.8" data-id="element-2165" data-mp="rlhxkvq8buq1" data-mpi={i} />)}
        </svg>
        <div className="absolute left-[46%] top-[20%] rounded-lg bg-white px-3 py-2.5 shadow-[0_10px_24px_-14px_rgba(20,20,60,0.5)]" data-id="element-2166" data-mp="wkl0oamnfqvd">
          <p className="flex items-center gap-1.5 font-sans text-[10.5px] font-semibold text-[#121212]" data-id="element-2167" data-mp="ilv54zfh9z94">
            <span className="w-3 h-3 rounded-full bg-[#4F46E5]" aria-hidden="true" data-id="element-2168" data-mp="mn6nqkzxpk7t" />
            {pick(districtInfo.name)}
          </p>
          {districtInfo.lines.map(l => <p key={l.label.fr} className="font-sans text-[9.5px] text-[#5B6478] mt-0.5 whitespace-nowrap" data-id="element-2169" data-mp="2h0prwdoh0123">
              <span className="font-semibold text-[#121212]" data-id="element-2170" data-mp="14q1d8qw3qops">{pick(l.value)}</span> {pick(l.label)}
            </p>)}
        </div>
        <div className="absolute right-2 bottom-2 rounded-md bg-white shadow-sm flex flex-col" aria-hidden="true" data-id="element-2171" data-mp="zu2vvn96y6yv">
          <PlusIcon className="w-5 h-5 p-1 text-[#3A4050]" data-id="element-2172" data-mp="q26kxbm0amd8" />
          <span className="h-px bg-[#EEEEF4]" data-id="element-2173" data-mp="q1ndiotzfgdg" />
          <MinusIcon className="w-5 h-5 p-1 text-[#3A4050]" data-id="element-2174" data-mp="m0blkcnmyj23" />
        </div>
      </div>
    </div>;
}
export function TopicInsight({
  isFR
}: P) {
  const pick = pk(isFR);
  return <div className="h-full rounded-xl border border-[#EEEEF4] bg-white p-4 flex flex-col" data-id="element-2175" data-mp="h3uy7ko9g8q2">
      <p className="font-sans text-[13.5px] font-semibold text-[#121212]" data-id="element-2176" data-mp="c4i209bi1pat">{isFR ? 'Sujets qui suscitent le plus d’interactions' : 'Most engaging topics'}</p>
      <table className="w-full mt-3 font-sans text-[11px]" data-id="element-2177" data-mp="26fdli6o82osd">
        <thead data-id="element-2178" data-mp="21hbzbcvi29co">
          <tr className="text-[9.5px] text-[#6B7180]" data-id="element-2179" data-mp="21gty01tzd4xw">
            <th className="text-left font-normal pb-1.5" colSpan={2} data-id="element-2180" data-mp="21f91w7s0pdxx">
              <span className="sr-only" data-id="element-2181" data-mp="29xkqz5rv9n94">{isFR ? 'Sujet' : 'Topic'}</span>
            </th>
            <th className="text-right font-normal pb-1.5 px-1" data-id="element-2182" data-mp="2dyw96q8ev67a">{isFR ? 'Ouv.' : 'Open'}</th>
            <th className="text-right font-normal pb-1.5 px-1" data-id="element-2183" data-mp="2iqeaxgi9datb">{isFR ? 'Clics' : 'Clicks'}</th>
            <th className="text-right font-normal pb-1.5 pl-1" data-id="element-2184" data-mp="2nq0mllifuj00">Questions</th>
          </tr>
        </thead>
        <tbody data-id="element-2185" data-mp="1r09q5gql6lrk">
          {topics.map((t, i) => <tr key={t.label.fr} className="text-[#121212]" data-id="element-2186" data-mp="1r1unfe4acit4" data-mpi={i}>
              <td className="py-1.5 pr-2 w-4 text-[#6B7180]" data-id="element-2187" data-mp="1v1v2n0z7ky18">{i + 1}</td>
              <td className="py-1.5 pr-2" data-id="element-2188" data-mp="1zzwwmgy6ahrh">{pick(t.label)}</td>
              <td className="py-1.5 px-1 text-right tabular-nums whitespace-nowrap" data-id="element-2189" data-mp="202j3sfh5iv96">{pct(isFR, t.open)}</td>
              <td className="py-1.5 px-1 text-right tabular-nums whitespace-nowrap" data-id="element-2190" data-mp="3bfbt7jmgz7m2">{pct(isFR, t.clicks)}</td>
              <td className="py-1.5 pl-1 text-right" data-id="element-2191" data-mp="3ge68ibl8ae1f">
                <span className="inline-block rounded bg-[#EEEDFD] px-2 py-0.5 text-[#4F46E5] tabular-nums whitespace-nowrap" data-id="element-2192" data-mp="3gcv88lqzrof2">{pick(t.questions)}</span>
              </td>
            </tr>)}
        </tbody>
      </table>
      <div className="mt-auto pt-3" data-id="element-2193" data-mp="19dp0wmtlfxi4">
        <AtnaRecommendation isFR={isFR} data-id="element-2194" data-mp="1e6i61bsz57uq" />
      </div>
    </div>;
}
export function AtnaRecommendation({
  isFR
}: P) {
  const pick = pk(isFR);
  return <div className="rounded-xl bg-[#F4F3FD] px-3.5 py-3 flex items-center gap-3" data-id="element-2195" data-mp="1ia6d7tdp21fg">
      <span className="w-8 h-8 rounded-md bg-[#4F46E5] flex items-center justify-center font-sans text-[11px] font-bold text-white shrink-0" aria-hidden="true" data-id="element-2196" data-mp="ljxcq5sxuloj">
        {isFR ? 'IA' : 'AI'}
      </span>
      <div className="flex-1 min-w-0" data-id="element-2197" data-mp="uhg6bykne8c1">
        <p className="font-sans text-[12px] font-medium text-[#4F46E5]" data-id="element-2198" data-mp="zhlwtbf6losj">{pick(analyticsReco.title)}</p>
        <p className="font-sans text-[12px] text-[#121212] leading-snug mt-0.5" data-id="element-2199" data-mp="25yftkfs51wz7">{pick(analyticsReco.body)}</p>
      </div>
      <button type="button" aria-label={pick(analyticsReco.action)} className="w-9 h-9 rounded-full bg-white shadow-[0_6px_14px_-8px_rgba(79,70,229,0.6)] flex items-center justify-center shrink-0 transition-colors duration-150 hover:bg-[#EEEDFD]" data-id="element-2200" data-mp="2avzkog9xq48k">
        <ArrowRightIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={2} aria-hidden="true" data-id="element-2201" data-mp="2bamknwg8ok2b" />
      </button>
    </div>;
}